import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import DOMPurify from 'dompurify';

const fonts = ['Arial', 'Calibri', 'Georgia', 'Tahoma', 'Times New Roman', 'Verdana', 'Courier New'];
const sizes = ['10px', '13px', '16px', '18px', '24px', '32px', '48px'];
const styleProperties = ['font-family', 'font-size', 'line-height', 'text-align', 'font-weight', 'font-style', 'text-decoration', 'text-decoration-line'];
const alignmentIcon = (alignment: 'left' | 'center' | 'right' | 'justify') => <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
  {[3, 7, 11, 15].map((y, index) => {
    const width = alignment === 'justify' || index % 2 === 0 ? 14 : 9;
    const x = alignment === 'right' ? 16 - width : alignment === 'center' ? (18 - width) / 2 : 2;
    return <line key={y} x1={x} x2={x + width} y1={y} y2={y} stroke="currentColor" strokeWidth="1.3" />;
  })}
</svg>;
export const cleanEmailHtml = (html: string) => {
  const safe = DOMPurify.sanitize(html, {
  ALLOWED_TAGS: ['p', 'br', 'strong', 'b', 'em', 'i', 'u', 'ul', 'ol', 'li', 'blockquote', 'a', 'div', 'span'],
    ALLOWED_ATTR: ['href', 'style', 'face', 'size'], ADD_TAGS: ['font'], ALLOW_DATA_ATTR: false,
  });
  const container = document.createElement('div'); container.innerHTML = safe;
  container.querySelectorAll('font').forEach(node => {
    const span = document.createElement('span'); span.innerHTML = node.innerHTML;
    span.style.cssText = node.getAttribute('style') || '';
    const face = node.getAttribute('face'); const size = Number(node.getAttribute('size'));
    if (face) span.style.fontFamily = face;
    if (size >= 1 && size <= 7) span.style.fontSize = sizes[size - 1];
    node.replaceWith(span);
  });
  container.querySelectorAll<HTMLElement>('*').forEach(node => {
    node.removeAttribute('face'); node.removeAttribute('size');
    if (node.hasAttribute('href') && !/^(https:\/\/|mailto:)/i.test(node.getAttribute('href') || '')) node.removeAttribute('href');
    const supported = document.createElement('span').style;
    styleProperties.forEach(property => {
      let value = node.style.getPropertyValue(property);
      if (property === 'font-size') {
        const index = ['x-small', 'small', 'medium', 'large', 'x-large', 'xx-large', 'xxx-large'].indexOf(value);
        if (index >= 0) value = sizes[index];
      }
      // Formatting only: reject CSS functions, URLs, escapes, and custom properties.
      if (value && /^[a-z0-9\s.,'"%-]+$/i.test(value)) supported.setProperty(property, value);
    });
    node.removeAttribute('style');
    if (supported.cssText) node.setAttribute('style', supported.cssText);
  });
  return container.innerHTML;
};

export default function EmailBodyEditor({ value, fields, onChange, ariaLabelledBy }: { value: string; fields: string[]; onChange: (html: string) => void; ariaLabelledBy?: string }) {
  const editorId = useId();
  const editor = useRef<HTMLDivElement>(null);
  const selection = useRef<Range | null>(null);
  const emitted = useRef<string | null>(null);
  const history = useRef<string[]>([]);
  const historyIndex = useRef(0);
  const [, updateHistory] = useState(0);
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkError, setLinkError] = useState('');
  const [activeCommands, setActiveCommands] = useState<string[]>([]);
  const updateFormattingState = useCallback(() => {
    const current = window.getSelection();
    if (!current?.rangeCount || !editor.current?.contains(current.anchorNode)) return;
    const next = ['bold', 'italic', 'underline', 'justifyLeft', 'justifyCenter', 'justifyRight', 'justifyFull', 'insertUnorderedList', 'insertOrderedList']
      .filter(name => {
        try { return document.queryCommandState?.(name); } catch { return false; }
      });
    setActiveCommands(next);
  }, []);
  useEffect(() => {
    document.addEventListener('selectionchange', updateFormattingState);
    return () => document.removeEventListener('selectionchange', updateFormattingState);
  }, [updateFormattingState]);
  const chip = (field: string) => {
    const node = document.createElement('span'); node.className = 'email-field-chip';
    node.dataset.emailField = field; node.textContent = `##${field}##`; return node;
  };
  const load = (html: string) => {
    if (!editor.current) return;
    editor.current.innerHTML = cleanEmailHtml(html);
    const walker = document.createTreeWalker(editor.current, NodeFilter.SHOW_TEXT);
    const nodes: Text[] = []; while (walker.nextNode()) nodes.push(walker.currentNode as Text);
    nodes.forEach(node => {
      const text = node.textContent || ''; if (!/##([^#]+)##/.test(text)) return;
      const fragment = document.createDocumentFragment(); let end = 0;
      text.replace(/##([^#]+)##/g, (match, field: string, index: number) => {
        fragment.append(document.createTextNode(text.slice(end, index)), chip(field)); end = index + match.length; return match;
      });
      fragment.append(document.createTextNode(text.slice(end))); node.replaceWith(fragment);
    });
    selection.current = null;
  };
  useEffect(() => {
    if (value === emitted.current) return;
    load(value); history.current = [cleanEmailHtml(value)]; historyIndex.current = 0; updateHistory(v => v + 1);
    // External template changes reset the editor history.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
  const remember = () => {
    const current = window.getSelection();
    if (current?.rangeCount && editor.current?.contains(current.anchorNode)) selection.current = current.getRangeAt(0).cloneRange();
  };
  const emit = () => {
    const copy = editor.current?.cloneNode(true) as HTMLElement | undefined;
    emitted.current = cleanEmailHtml(copy?.innerHTML || '');
    if (history.current[historyIndex.current] !== emitted.current) {
      history.current = [...history.current.slice(0, historyIndex.current + 1), emitted.current];
      if (history.current.length > 100) history.current.shift();
      historyIndex.current = history.current.length - 1; updateHistory(v => v + 1);
    }
    onChange(emitted.current);
  };
  const restore = () => {
    editor.current?.focus(); const current = window.getSelection();
    if (selection.current && editor.current?.contains(selection.current.commonAncestorContainer)) { current?.removeAllRanges(); current?.addRange(selection.current); }
    else if (editor.current && (!current?.rangeCount || !editor.current.contains(current.anchorNode))) {
      const range = document.createRange(); range.selectNodeContents(editor.current); range.collapse(false);
      current?.removeAllRanges(); current?.addRange(range);
    }
  };
  const command = (name: string, argument?: string) => {
    restore(); document.execCommand('styleWithCSS', false, 'true');
    document.execCommand(name, false, argument); remember(); updateFormattingState(); emit();
  };
  const undoRedo = (direction: number) => {
    const index = historyIndex.current + direction;
    if (index < 0 || index >= history.current.length) return;
    historyIndex.current = index; emitted.current = history.current[index]; load(emitted.current);
    restore(); onChange(emitted.current); updateHistory(v => v + 1);
  };
  const lineSpacing = (value: string) => {
    restore(); if (!editor.current) return;
    const range = window.getSelection()?.getRangeAt(0); if (!range) return;
    let blocks = Array.from(editor.current.querySelectorAll<HTMLElement>('p, div, li, blockquote')).filter(node => range.intersectsNode(node));
    if (!blocks.length) {
      document.execCommand('formatBlock', false, 'p');
      blocks = Array.from(editor.current.querySelectorAll<HTMLElement>('p')).filter(node => window.getSelection()?.containsNode(node, true));
    }
    blocks.forEach(node => { node.style.lineHeight = value; }); remember(); emit();
  };
  const applyLink = () => {
    const url = linkUrl.trim();
    try { if (new URL(url).protocol !== 'https:') throw new Error(); }
    catch { setLinkError('Enter a valid HTTPS link.'); return; }
    restore();
    if (window.getSelection()?.isCollapsed) {
      const link = document.createElement('a'); link.href = url; link.textContent = url;
      command('insertHTML', link.outerHTML);
    } else command('createLink', url);
    setLinkOpen(false); setLinkError('');
  };
  const insert = (field: string) => {
    restore(); const current = window.getSelection(); const range = current?.rangeCount ? current.getRangeAt(0) : document.createRange();
    if (!editor.current) return;
    if (!editor.current.contains(range.commonAncestorContainer)) { range.selectNodeContents(editor.current); range.collapse(false); }
    range.deleteContents(); const node = chip(field); range.insertNode(node); range.setStartAfter(node); range.collapse(true);
    current?.removeAllRanges(); current?.addRange(range); remember(); emit();
  };
  return <>
    <div className="email-editor-toolbar" role="toolbar" aria-label="Email formatting" aria-controls={editorId}>
      <Form.Select size="sm" aria-label="Font" defaultValue="" onFocus={remember} onChange={e => { command('fontName', e.target.value); e.target.value = ''; }}>
        <option value="" disabled>Font</option>{fonts.map(font => <option key={font}>{font}</option>)}
      </Form.Select>
      <Form.Select size="sm" aria-label="Font size" defaultValue="" onFocus={remember} onChange={e => { command('fontSize', e.target.value); e.target.value = ''; }}>
        <option value="" disabled>Size</option>{sizes.map((size, index) => <option key={size} value={index + 1}>{size}</option>)}
      </Form.Select>
      <Form.Select size="sm" aria-label="Line spacing" defaultValue="" onFocus={remember} onChange={e => { lineSpacing(e.target.value); e.target.value = ''; }}>
        <option value="" disabled>Line spacing</option>{['1', '1.15', '1.5', '2', '2.5', '3'].map(value => <option key={value}>{value}</option>)}
      </Form.Select>
      {([['bold', 'Bold', <b>B</b>], ['italic', 'Italic', <i>I</i>], ['underline', 'Underline', <u>U</u>],
        ['justifyLeft', 'Align left', alignmentIcon('left')], ['justifyCenter', 'Align center', alignmentIcon('center')], ['justifyRight', 'Align right', alignmentIcon('right')], ['justifyFull', 'Justify', alignmentIcon('justify')],
        ['insertUnorderedList', 'Bullets', '• ≡'], ['insertOrderedList', 'Numbers', '1. ≡']] as const).map(([name, label, icon]) =>
        <Button key={name} type="button" size="sm" variant="outline-secondary" aria-label={label} aria-pressed={activeCommands.includes(name)} title={label} className={'email-format-button ' + name}
          onMouseDown={e => e.preventDefault()} onClick={() => command(name)}>{icon}</Button>)}
      <Button type="button" size="sm" variant="outline-secondary" onMouseDown={e => e.preventDefault()} onClick={() => {
        remember(); setLinkUrl(''); setLinkError(''); setLinkOpen(true);
      }} aria-label="Insert link" title="Insert link" aria-expanded={linkOpen}>Link</Button>
      <Button type="button" size="sm" variant="outline-secondary" onMouseDown={e => e.preventDefault()} onClick={() => command('unlink')} title="Remove link">Unlink</Button>
      <Button type="button" size="sm" variant="outline-secondary" onMouseDown={e => e.preventDefault()} onClick={() => undoRedo(-1)} disabled={historyIndex.current === 0} title="Undo (Ctrl+Z)">Undo</Button>
      <Button type="button" size="sm" variant="outline-secondary" onMouseDown={e => e.preventDefault()} onClick={() => undoRedo(1)} disabled={historyIndex.current >= history.current.length - 1} title="Redo (Ctrl+Y)">Redo</Button>
    </div>
    {linkOpen && <div className="email-editor-link" role="group" aria-label="Insert hyperlink">
      <Form.Control aria-label="Link URL" placeholder="https://example.com" autoFocus value={linkUrl}
        aria-invalid={!!linkError} aria-describedby={linkError ? `${editorId}-link-error` : undefined}
        onChange={e => { setLinkUrl(e.target.value); setLinkError(''); }} onKeyDown={e => {
          if (e.key === 'Enter') { e.preventDefault(); applyLink(); }
          if (e.key === 'Escape') { e.preventDefault(); setLinkOpen(false); restore(); }
        }} />
      <Button type="button" size="sm" onClick={applyLink}>Apply link</Button>
      <Button type="button" size="sm" variant="outline-secondary" onClick={() => { setLinkOpen(false); restore(); }}>Cancel link</Button>
      {linkError && <p id={`${editorId}-link-error`} role="alert" className="text-danger mb-0">{linkError}</p>}
    </div>}
    <div ref={editor} id={editorId} className="email-editor" contentEditable role="textbox" aria-label={ariaLabelledBy ? undefined : 'Email body'} aria-labelledby={ariaLabelledBy} aria-multiline="true" suppressContentEditableWarning
      onInput={() => { remember(); updateFormattingState(); emit(); }} onKeyUp={() => { remember(); updateFormattingState(); }} onMouseUp={() => { remember(); updateFormattingState(); }}
      onFocus={updateFormattingState}
      onBlur={remember} onKeyDown={e => {
        if ((e.ctrlKey || e.metaKey) && ['z', 'y'].includes(e.key.toLowerCase())) {
          e.preventDefault(); undoRedo(e.key.toLowerCase() === 'y' || e.shiftKey ? 1 : -1);
        }
      }}
      onPaste={e => { e.preventDefault(); command('insertText', e.clipboardData.getData('text/plain')); }} />
    <div className="email-template-fields" role="group" aria-label="Insert a field">{fields.map(field =>
      <Button key={field} type="button" size="sm" variant="outline-primary" onMouseDown={e => e.preventDefault()} onClick={() => insert(field)}>{field}</Button>)}</div>
  </>;
}
